import React, { useRef, useState } from "react";

/**
 * FAQAccordion smooth open/close animation (no Bootstrap JS)
 * Props:
 *  - items: [{ title: string, content: ReactNode }]
 * Behavior:
 *  - single-open accordion (opening one closes previous)
 *  - initial open index defaults to 0 to match your previous code
 */
const FAQAccordion = ({ items = [] }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const contentRefs = useRef([]);

  // keep refs array length in sync
  if (contentRefs.current.length !== items.length) {
    contentRefs.current = Array(items.length)
      .fill()
      .map((_, i) => contentRefs.current[i] || null);
  }

  const toggle = (index) => {
    const willOpen = index !== activeIndex;
    const prev = activeIndex;

    // close previous if different
    if (prev !== null && prev !== index) {
      collapsePanel(contentRefs.current[prev]);
    }

    if (willOpen) {
      expandPanel(contentRefs.current[index]);
      setActiveIndex(index);
    } else {
      collapsePanel(contentRefs.current[index]);
      setActiveIndex(null);
    }
  };

  const expandPanel = (el) => {
    if (!el) return;
    // ensure visible to measure
    el.style.display = "block";

    // target height
    const target = el.scrollHeight;

    // start from 0px
    el.style.height = "0px";
    // force reflow (ESLint-safe)
    el.getBoundingClientRect();

    el.style.transition = "height 350ms ease";
    el.style.height = `${target}px`;

    const onEnd = (ev) => {
      if (ev.propertyName !== "height") return;
      // cleanup to allow responsive height
      el.style.transition = "height 350ms ease";
      el.style.height = "";

      el.removeEventListener("transitionend", onEnd);
    };
    el.addEventListener("transitionend", onEnd);
  };

  const collapsePanel = (el) => {
    if (!el) return;
    // compute current pixel height
    const curr = el.scrollHeight;
    el.style.height = `${curr}px`;
    // force reflow
    el.getBoundingClientRect();

    el.style.transition = "height 350ms ease";
    requestAnimationFrame(() => {
      el.style.height = "0px";
    });

    const onEnd = (ev) => {
      if (ev.propertyName !== "height") return;
      // el.style.display = "none";
      el.style.transition = "height 350ms ease";
      el.style.height = "0px";
      el.removeEventListener("transitionend", onEnd);
    };
    el.addEventListener("transitionend", onEnd);
  };

  return (
    <div className="accordion faq" id="accordionExample2">
      {items.map((item, index) => {
        const isActive = index === activeIndex;
        return (
          <div className="accordion-item" key={index}>
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${isActive ? "" : "collapsed"} ${
                  index === 0 ? "pt-0" : ""
                }`}
                type="button"
                onClick={() => toggle(index)}
                aria-expanded={isActive ? "true" : "false"}
                aria-controls={`faq-body-${index}`}
                id={`faq-heading-${index}`}
              >
                {item.title}
              </button>
            </h2>

            <div
              id={`faq-body-${index}`}
              role="region"
              aria-labelledby={`faq-heading-${index}`}
              className={`accordion-collapse collapse ${isActive ? "show" : ""}`}
              ref={(el) => (contentRefs.current[index] = el)}
              style={{
                // display: isActive ? "block" : "none",
                overflow: "hidden",
                height: isActive ? "110px" : "0px",
                transition: "height 350ms ease",
              }}
            >
              <div className="accordion-body">{item.content}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FAQAccordion;
